import { ColorType, ThemeColor } from "./enums";

//Theme types
export type ThemeColors = {
  brand1: string;
  brand2: string;
  brand3: string;
  brand4: string;
  secondary1: string;
  secondary2: string;
  secondary3: string;
  secondary4: string;
  base1: string;
  base2: string;
  base3: string;
  base4: string;
  base5: string;
  accent1: string;
  accent2: string;
  general: {
    primaryBg: string;
    secondaryBg: string;
  };
  text: {
    heading1: string;
    heading2: string;
    heading3: string;
    heading4: string;
    heading5: string;
    heading6: string;
    paragraph1: string;
    paragraph2: string;
    paragraph3: string;
  };
  buttons: {
    primary: {
      default: {
        background: string;
        text: string;
        border: string;
      };
      hover: {
        background: string;
        text: string;
        border: string;
      };
    };
    secondary: {
      default: {
        background: string;
        text: string;
        border: string;
      };
      hover: {
        background: string;
        text: string;
        border: string;
      };
    };
    tertiary: {
      default: {
        background: string;
        text: string;
        border: string;
      };
      hover: {
        background: string;
        text: string;
        border: string;
      };
    };
  };
  formField: {
    fill: string;
    border: string;
    text: string;
    label: string;
  };
};

export type FontValues = {
  fontSize: number;
  url?: string;
  weight?: string;
  isItalic?: boolean;
  typeFace?: string;
  fontFamily?: string;
  lineHeight?: number;
  letterSpacing?: number;
  isAutomatic?: boolean;
};

export type ThemeTypography = {
  [key: string]: {
    headings: {
      default: FontValues;
      h1: FontValues;
      h2: FontValues;
      h3: FontValues;
      h4: FontValues;
      h5: FontValues;
      h6: FontValues;
    };
    paragraphs: {
      default: FontValues;
      p1: FontValues;
      p2: FontValues;
      p3: FontValues;
    };
    buttons: {
      default: FontValues;
      primary: FontValues;
      secondary: FontValues;
      tertiary: FontValues;
    };
  };
};

export type ButtonBorder = {
  width: string;
  borderRadiusAll: number;
  borderStartStartRadius: number;
  borderStartEndRadius: number;
  borderEndStartRadius: number;
  borderEndEndRadius: number;
};

export type ThemeButtons = {
  primary: {
    padding: string;
    border: ButtonBorder;
  };
  secondary: {
    padding: string;
    border: ButtonBorder;
  };
  tertiary: {
    padding: string;
    border: ButtonBorder;
  };
};
export type ThemeForm = {
  form: {
    style: string;
    size: string;
    fill: boolean;
    border: {
      style: string;
    };
  };
};
export type ThemeAnimation = {
  id: number;
  name: string;
};
export type ThemeContent = {
  colors: ThemeColors;
  typography: ThemeTypography;
  buttons: ThemeButtons;
  form: ThemeForm;
  animation: ThemeAnimation;
};

export type Theme = {
  id: string;
  name: string;
  content: ThemeContent;
};

export type SiteTheme = {
  theme_id: string;
  site_theme_id: string;
  theme: Theme;
  reference_theme: ThemeContent;
};

//Raw data types
export type RawData = {
  utility_pages: UtilityPage[];
  website_id: number;
  plan: Plan;
  domain_name: { type: string; domainName: string; active: boolean };
  expiry_date: Date | null;
  logo: string | null;
  favicon: string;
  color: { pallet_id: number; slider_value: number; colors: Colors };
  locale: {
    ar: LocaleDataType;
    en: LocaleDataType;
  };
  universal_data: { industry: string };
  universal_sections: UniversalSections;
  pages: (CommonPagesType & {
    locale: Locale;
    path: string;
    sort_order: number;
  })[];
  animation: Animation;
  defaultLocale: Locale;
  locales: Locale[];
  pageToSlugMap: { [key: string]: string };
  menu_items: MenuItem[];
  customCodes: CustomCodes;
  isUnderMaintenance: boolean;
  theme: SiteTheme;
};

export type PageProps = {
  websiteId: string;
  page: Page;
  plan: Plan;
  colors: Colors;
  universalData: UniversalData;
  universalSections: UniversalSections;
  defaultLocale: Locale;
  currentLocale: Locale;
  pages: PageMeta[];
  trackingCodes: TrackingCodes;
  animation: Animation;
  customCodes: CustomCodes;
  isUnderMaintenance: boolean;
  maintenancePages: UtilityPage[];
  theme: ThemeContent;
};

export interface SiteDataType {
  id: string;
  data: RawData;
  domain: string;
  isExpired: boolean;
  trackingCodes: TrackingCodes;
  rendererVersion: string;
  version: number;
  stats: any;
  customCodes: CustomCodes;
  isUnderMaintenance: boolean;
  theme: SiteTheme;
}

// Page props types

export type Locale = "en" | "ar";

enum Destination {
  Internal = "internal",
  External = "external",
}

export type CommonType = {
  id: string;
  type: string;
  value: string;
};

type CommonValueType = {
  destination: Destination.External;
  href: string;
  pageId: string | null;
  target: string;
  text: string;
};

// type ImageType = Omit<CommonType, "value"> & {
//   value: {
//     id: string;
//     type: string;
//     url: string;
//     website_id: string;
//   };
// };

export type Page = {
  id: string;
  title: string;
  name: string;
  sort_order: number;
  slug: string;
  seo_title: string;
  seo_keywords: string;
  seo_description: string;
  open_graph_title: string;
  open_graph_description: string;
  open_graph_image: string;
  path: string;
  parent_id: string | null;
  locale: Locale;
  language: Locale;
  home_page: boolean;
  custom_js_head: null;
  custom_js_body: null;
  add_to_header: boolean;
  add_to_footer: boolean;
  page_sections: {
    category_id: number;
    id: string;
    reference_id: boolean;
    section_design: string;
    section_order: number;
    section_value: {
      section_action: Omit<CommonType, "value"> & {
        value: CommonValueType;
      };
      section_content: CommonType;
      section_image: CommonType;
      section_image2: CommonType;
      section_title: CommonType;
    };
    universal: boolean;
    universal_section_id: string | null;
    section_settings: SectionSettings;
  }[];
};

export type MenuItem = {
  id: string;
  language: Locale;
  level: number;
  menu: string;
  menu_id: number;
  parent_id: string | null;
  site_id: number;
  sort_order: number;
  title: string;
  type: string;
  type_id: number;
  value: string;
};

export type Plan = {
  id: number;
  name: string;
};

export type Colors = string[];

export type Font = {
  type: string;
  fontFamily: string;
  weight: string;
  isItalic: boolean;
  url: string;
};

export type LocaleDataType = {
  active: boolean;
  default_locale: boolean;
  fonts: {
    body_font: Font;
    headline_font: Font;
    variation_id: number;
  };
};

export type CommonPagesType = Pick<
  Page,
  | "id"
  | "home_page"
  | "language"
  | "name"
  | "parent_id"
  | "slug"
  | "title"
  | "page_sections"
>;

export type UniversalData = {
  favicon: string;
  languages: {
    active: boolean;
    iso: string;
    link: string;
  }[];
  locales: {
    ar: LocaleDataType;
    en: LocaleDataType;
  };
  logo: string | null;
  pages: Page[];
  menuItems: (Page | MenuItem)[];
};

type UniversalSectionElement = {
  category_id: number;
  id: string;
  referenece_id: boolean;
  section_design: string;
  section_order: number;
  universal: boolean;
  universal_section_id: number;
};

export type FooterSectionValue = {
  copyright_text: CommonType;
  logo: CommonType;
  social_links: Omit<CommonType, "value"> & {
    value: { facebook: string; twitter: string };
  };
  text: CommonType;
  title_text: CommonType;
};

export type GlobalNavbarSectionSettings = {
  background: any;
  style: {
    isSticky: boolean;
    isTransparent: boolean;
  };
  sticky: {
    isAlternateLogo: boolean;
    backgroundColor: string;
    navLinksColor: string;
    stickyLogoSize: string;
    stickyLogoUrl: string;
  };
  colors: {
    backgroundColor: string;
    navLinksColor: string;
  };
};

export type GlobalNavbarSectionValue = {
  logo: CommonType;
  email: Omit<CommonType, "value"> & {
    value: CommonValueType;
  };
  email_icon: CommonType;
  phone: Omit<CommonType, "value"> & {
    value: CommonValueType;
  };
  phone_icon: CommonType;
  socialLinks: Omit<CommonType, "value"> & {
    value: { facebook: string; twitter: string };
  };
};

export type UniversalSections = {
  footer: {
    ar: UniversalSectionElement & {
      section_value: FooterSectionValue;
      section_settings: FooterSectionSettings;
    };
    en: UniversalSectionElement & {
      section_value: FooterSectionValue;
      section_settings: FooterSectionSettings;
    };
  };
  header_expanded: {
    ar: UniversalSectionElement & {
      section_value: GlobalNavbarSectionValue;
      section_settings: GlobalNavbarSectionSettings;
    };
    en: UniversalSectionElement & {
      section_value: GlobalNavbarSectionValue;
      section_settings: GlobalNavbarSectionSettings;
    };
  };
};

export type TrackingCodes =
  | {
      googleAnalytics: string | undefined;
      facebookPixel: string | undefined;
      googleTagManager: string | undefined;
      tiktokPixel: string | undefined;
      snapchatPixel: string | undefined;
    }
  | Record<string, never>;

export type CustomCodes =
  | {
      bodyCode: string | undefined;
      headCode: string | undefined;
    }
  | Record<string, never>;

export type Animation =
  | {
      id: number;
      name: string;
      slug?: string;
    }
  | undefined;

export type SectionSettings = {
  background: {
    color: string;
    image: string;
    overlay: string;
  };
};

export type FooterSectionSettingsType = {
  color: string;
  colorType: ColorType;
  themeColorKey: ThemeColor;
};

export type FooterSectionSettings = {
  background: FooterSectionSettingsType;
  linksColor: FooterSectionSettingsType;
};

export type UtilityPageSection = {
  id: string;
  page_id: string;
  section_order: number;
  reference_id: boolean;
  category_id: number;
  section_design: string;
  universal_section_id: string | null;
  universal: boolean;
  section_value: {
    section_action: Omit<CommonType, "value"> & {
      value: CommonValueType;
    };
    section_content: CommonType;
    section_image: CommonType;
    section_image2: CommonType;
    section_title: CommonType;
  };
  section_settings: SectionSettings;
};

export type UtilityPage = {
  id: string;
  name: string;
  type: "NOT_FOUND_PAGE" | "UNDER_MAINTENANCE_PAGE";
  language: string;
  page_sections: UtilityPageSection[];
};

export type PageMeta = {
  id: string;
  language: Locale;
  locale: Locale;
  parent_id: string | null;
  path: string;
  slug: string;
  name: string;
  title: string;
  sort_order: number;
  home_page: boolean;
};

export type Params = {
  domain: string;
  slugs?: string[];
};
